import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from './api.service';
import { FilterService } from './filter.service';
import { NewFilterService } from './new-filter.service';
import { friendlyApiErrorMessage } from './api-error-notifier.service';

export interface InteractiveFilter {
  sourceWidgetKey: number;
  column: string;
  value: any;
  operator: 'equals' | 'in';
}

export interface WidgetApiConfig {
  apiKey: string;
  endpoint: string;
  widgetKey: number;
  /** 'page' = shared page-level call, 'widget' = individual widget call */
  level: 'page' | 'widget';
}

@Injectable({
  providedIn: 'root',
})
export class PageDataService implements OnDestroy {

  /** Raw API responses keyed by api_key */
  private masterData = new Map<string, any[]>();

  /** Friendly error message for each api_key whose last fetch failed */
  private fetchErrors = new Map<string, string>();

  /** Per-widget observable streams keyed by api_key */
  private dataStreams = new Map<string, BehaviorSubject<any[]>>();

  /** Currently active cross-filters */
  activeFilters: InteractiveFilter[] = [];

  /** Active filters as observable for visual feedback in widgets */
  activeFilters$ = new BehaviorSubject<InteractiveFilter[]>([]);

  /** Page-level loading state */
  loading$ = new BehaviorSubject<boolean>(false);

  /** All widget API configs for this page */
  private widgetApiConfigs: WidgetApiConfig[] = [];

  /** Common data stream key used when widgets share the same dataset */
  private static readonly COMMON_KEY = '__common__';

  /** Whether PageDataService is actively managing data for the current page */
  private _isActive = false;
  get isActive(): boolean { return this._isActive; }

  /** Guard to cancel in-flight fetch when a new one starts */
  private activeFetchSub: Subscription | null = null;

  private subscriptions: Subscription[] = [];
  
  toggleData: any[] = [];
  
  constructor(
    private httpClient: HttpClient,
    private apiService: ApiService,
    private filterService: FilterService,
    private newFilterService: NewFilterService,
  ) {}

  /**
   * Reset all state (called when switching pages/tabs).
   */
  reset(): void {
    this._isActive = false;
    if (this.activeFetchSub) {
      this.activeFetchSub.unsubscribe();
      this.activeFetchSub = null;
    }
    for (const stream of this.dataStreams.values()) {
      stream.complete();
    }
    this.dataStreams.clear();
    this.masterData.clear();
    this.fetchErrors.clear();
    this.widgetApiConfigs = [];
    this.activeFilters = [];
    this.activeFilters$.next([]);
    this.loading$.next(false);
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  /**
   * Extract all unique api_key values from widget configs and create BehaviorSubjects.
   * Then fetch all data.
   */
  initializePage(widgets: any[], baseQuery: any, pageApiKey?: string): void {
    this.reset();
    this._isActive = true;
    const seen = new Set<string>();
    // Register page-level API key if provided (shared data for all widgets)
    if (pageApiKey) {
      const key = String(pageApiKey);
      if (!seen.has(key)) {
        seen.add(key);
        this.widgetApiConfigs.push({ apiKey: key, endpoint: this.apiService.executionUrl, widgetKey: 0, level: 'page' });
      }
    }

    console.log('[PageDataService] initializePage — scanning', widgets.length, 'widgets');

    for (const widget of widgets) {
      //this.scanWidgetForApiKeys(widget, seen, widget?.key);

      // Nested widgets (e.g. key 100807) hold inner widgets in `nested_widget_config`.
      // Recurse so their api_keys are registered against the outer widget's key.
      const nestedConfigs = widget?.nested_widget_config;
      if (Array.isArray(nestedConfigs) && nestedConfigs.length > 0) {
        console.log('[PageDataService]   widget', widget?.key, 'has', nestedConfigs.length, 'nested widget(s) — scanning');
        for (const nested of nestedConfigs) {
          this.scanWidgetForApiKeys(nested, seen, widget?.key);
        }
      }else{
        this.scanWidgetForApiKeys(widget, seen, widget?.key);
      }
    }
    // Create BehaviorSubjects for each unique api_key
    for (const config of this.widgetApiConfigs) {
      if (!this.dataStreams.has(config.apiKey)) {
        this.dataStreams.set(config.apiKey, new BehaviorSubject<any[]>([]));
      }
    }

    // Always create a common data stream
    if (!this.dataStreams.has(PageDataService.COMMON_KEY)) {
      this.dataStreams.set(PageDataService.COMMON_KEY, new BehaviorSubject<any[]>([]));
    }

    console.log('[PageDataService] initializePage — FINAL widgetApiConfigs:', this.widgetApiConfigs.map(c => ({ apiKey: c.apiKey, widgetKey: c.widgetKey, level: c.level })));
    console.log('[PageDataService] initializePage — unique api_keys to fetch:', Array.from(seen));
    console.log('[PageDataService] initializePage — baseQuery keys:', baseQuery ? Object.keys(baseQuery).length : 0);

    // Fetch immediately if we have a valid baseQuery
    if (this.widgetApiConfigs.length > 0 && baseQuery && Object.keys(baseQuery).length > 0) {
      this.fetchAllData(baseQuery, this.filterService.showByKey);
    } else {
      console.log('[PageDataService] No API keys or empty baseQuery — waiting for filter changes');
      this.loading$.next(false);
    }

    // Subscribe to filter changes so PageDataService re-fetches automatically
    this.subscriptions.push(
      this.filterService.filterQuery.subscribe(() => {
        if (this.widgetApiConfigs.length > 0) {
          // Keep the Show By selection; without it a filter change falls back to the default report type
          this.fetchAllData(this.filterService.baseQuery, this.filterService.showByKey);
        }
      })
    );

    // Show By toggle: re-fetch page data with the selected report type
    this.subscriptions.push(
      this.newFilterService.showBy.subscribe((reportType: any) => {
        if (this.widgetApiConfigs.length > 0) {
          this.fetchAllData(this.filterService.baseQuery, reportType);
        }
      })
    );
  }

  /**
   * Scan a single widget's config for api_keys (list_of_api + KPI fields)
   * and register them. Used for both top-level and nested widgets — nested
   * ones register under their outer widget's key so cross-filtering still
   * targets the right host.
   */
  private scanWidgetForApiKeys(widget: any, seen: Set<string>, ownerWidgetKey: number): void {
    const cfg = widget?.config;
    if (!cfg) {
      console.log('[PageDataService]   widget', widget?.key, 'SKIPPED (no config)');
      return;
    }

    if (cfg['data_mode'] === 'widget') {
      console.log('[PageDataService]   widget', widget?.key, 'SKIPPED (widget mode)');
      return;
    }

    const endpoint = cfg['widget_endpoint'] || cfg['list_of_api_endpoint'] || this.apiService.executionUrl;

    const listOfApi = cfg['list_of_api'];
    console.log('[PageDataService]   widget', widget?.key, 'list_of_api:', listOfApi);
    if (listOfApi) {
      const key = String(listOfApi?.api_key ?? listOfApi);
      if (key && key !== 'undefined' && !seen.has(key)) {
        seen.add(key);
        this.widgetApiConfigs.push({ apiKey: key, endpoint, widgetKey: ownerWidgetKey, level: 'widget' });
        console.log('[PageDataService]   widget', widget?.key, '→ REGISTERED api_key:', key, '(widget-level, owner:', ownerWidgetKey, ')');
      } else if (seen.has(key)) {
        console.log('[PageDataService]   widget', widget?.key, '→ DEDUPED api_key:', key, '(already registered)');
      } else {
        console.log('[PageDataService]   widget', widget?.key, '→ INVALID api_key:', key);
      }
    }

    // Hierarchical grid reads its key from list_of_api_parent, so register it too
    const kpiFields = ['list_of_api_parent', 'kpi_1_value_api', 'kpi_2_value_api', 'kpi_value_api', 'custom_kpi_value_api'];
    for (const field of kpiFields) {
      const raw = cfg[field];
      if (raw == null) continue;
      const key = String(typeof raw === 'object' ? raw.api_key : raw);
      if (key && key !== 'undefined' && !seen.has(key)) {
        seen.add(key);
        this.widgetApiConfigs.push({ apiKey: key, endpoint, widgetKey: ownerWidgetKey, level: 'widget' });
        console.log('[PageDataService]   widget', widget?.key, '→ REGISTERED api_key:', key, 'from', field, '(widget-level, owner:', ownerWidgetKey, ')');
      }
    }
  }

  /**
   * Fetch all data in parallel using forkJoin.
   * Separates page-level (shared) API call from widget-level (individual) API calls.
   * Clears interactive filters since master data is changing.
   * `reportType` overrides filterService.report_type (e.g. from the Show By toggle).
   */
  fetchAllData(baseQuery: any, reportType?: any): void {
    if (!baseQuery || Object.keys(baseQuery).length === 0) {
      console.log('[PageDataService] No base query — skipping fetch');
      this.loading$.next(false);
      return;
    }

    if (this.widgetApiConfigs.length === 0) {
      console.log('[PageDataService] No API keys configured — skipping fetch');
      this.loading$.next(false);
      return;
    }

    // Cancel any in-flight fetch to prevent duplicate API calls
    if (this.activeFetchSub) {
      this.activeFetchSub.unsubscribe();
      this.activeFetchSub = null;
    }

    this.loading$.next(true);
    this.fetchErrors.clear();
    this.activeFilters = [];
    this.activeFilters$.next([]);

    // Split configs into page-level and widget-level
    const pageConfigs = this.widgetApiConfigs.filter(c => c.level === 'page');
    const widgetConfigs = this.widgetApiConfigs.filter(c => c.level === 'widget');

    console.log('[PageDataService] fetchAllData — page-level:', pageConfigs.length, ', widget-level:', widgetConfigs.length);

    const requests: { [apiKey: string]: Observable<any> } = {};

    // 1. Page-level API call (shared data for all widgets)
    for (const config of pageConfigs) {
      const query = Object.assign({}, baseQuery, {
        api_key: config.apiKey,
        report_typ: reportType ? reportType : this.filterService.report_type,
        module_key: this.apiService.getModuleKey(),
      });
      const url = `${this.apiService.apiBaseUrl}/${config.endpoint}`;
      console.log('[PageDataService]   → PAGE-LEVEL POST', url, 'api_key:', config.apiKey);

      requests[config.apiKey] = this.httpClient
        .post(url, query)
        .pipe(catchError((err) => {
          console.warn('[PageDataService]   ✗ PAGE api_key', config.apiKey, 'FAILED:', err?.status, err?.message);
          this.fetchErrors.set(config.apiKey, friendlyApiErrorMessage(err));
          return of([]);
        }));
    }

    // 2. Widget-level API calls (individual data per widget)
    for (const config of widgetConfigs) {
      if (requests[config.apiKey]) continue; // already queued (shared api_key with page)
      const query = Object.assign({}, baseQuery, {
        api_key: config.apiKey,
        report_typ: reportType ? reportType : this.filterService.report_type,
        module_key: this.apiService.getModuleKey(),
      });
      const url = `${this.apiService.apiBaseUrl}/${config.endpoint}`;
      console.log('[PageDataService]   → WIDGET-LEVEL POST', url, 'api_key:', config.apiKey, 'widgetKey:', config.widgetKey);

      requests[config.apiKey] = this.httpClient
        .post(url, query)
        .pipe(catchError((err) => {
          console.warn('[PageDataService]   ✗ WIDGET api_key', config.apiKey, 'FAILED:', err?.status, err?.message);
          this.fetchErrors.set(config.apiKey, friendlyApiErrorMessage(err));
          return of([]);
        }));
    }

    console.log('[PageDataService] fetchAllData — firing forkJoin with keys:', Object.keys(requests));

    if (Object.keys(requests).length === 0) {
      console.log('[PageDataService] No requests to fire — skipping fetch');
      this.loading$.next(false);
      return;
    }

    const pageApiKeys = new Set(pageConfigs.map(c => c.apiKey));

    const sub = forkJoin(requests).subscribe({
      next: (results: { [apiKey: string]: any }) => {
        console.log('[PageDataService] forkJoin response received. Keys:', Object.keys(results));

        for (const [apiKey, data] of Object.entries(results)) {
          const result = data == null ? [] : (Array.isArray(data) ? data : [data]);
          const level = pageApiKeys.has(apiKey) ? 'PAGE' : 'WIDGET';
          console.log('[PageDataService]   ←', level, 'api_key', apiKey, 'rows:', result.length);

          this.masterData.set(apiKey, result);
          const stream = this.dataStreams.get(apiKey);
          if (stream) {
            stream.next(result);
          }

          // Page-level data is also pushed to the common stream for shared access
          if (pageApiKeys.has(apiKey)) {
            this.masterData.set(PageDataService.COMMON_KEY, result);
            const commonStream = this.dataStreams.get(PageDataService.COMMON_KEY);
            if (commonStream) commonStream.next(result);
          }
        }

        this.loading$.next(false);
      },
      error: (err) => {
        console.error('[PageDataService] API error:', err);
        this.loading$.next(false);
      },
    });

    this.activeFetchSub = sub;
    this.subscriptions.push(sub);
  }

  /**
   * Error message from the last fetch of this api_key ('' when it succeeded).
   * Without a key, returns the page-level (common stream) error.
   */
  getFetchError(apiKey?: string | number): string {
    if (apiKey != null && apiKey !== '' && this.dataStreams.has(String(apiKey))) {
      return this.fetchErrors.get(String(apiKey)) || '';
    }
    const pageKey = this.widgetApiConfigs.find(c => c.level === 'page')?.apiKey;
    return pageKey ? this.fetchErrors.get(pageKey) || '' : '';
  }

  /**
   * Get the data stream for a specific api_key.
   * Falls back to common data stream if the api_key has no dedicated stream with data.
   */
  getDataStream(apiKey: string): Observable<any[]> {
    if (this.dataStreams.has(apiKey)) {
      return this.dataStreams.get(apiKey)!.asObservable();
    }
    // Fallback to common data stream
    return this.getCommonDataStream();
  }

  filterPageData(data: any): any {
    // debugger
    let cfg = this.filterService.pageChangeDisplays;
    if(cfg && cfg.findVar) {
      return data.filter((f:any) => String(f[cfg.findVar]) === String(cfg.selectedType))
    } else {
      return data;
    }
  }

  /**
   * Get the common/shared data stream (used when all widgets share the same dataset).
   */
  getCommonDataStream(): Observable<any[]> {
    if (!this.dataStreams.has(PageDataService.COMMON_KEY)) {
      this.dataStreams.set(PageDataService.COMMON_KEY, new BehaviorSubject<any[]>([]));
    }
    return this.dataStreams.get(PageDataService.COMMON_KEY)!.asObservable();
  }

  /**
   * Toggle an interactive filter.
   * If same source+column+value exists, remove it (deselect).
   * Otherwise add it.
   */
  applyInteractiveFilter(filter: InteractiveFilter): void {
    const existingIndex = this.activeFilters.findIndex(
      f => f.sourceWidgetKey === filter.sourceWidgetKey
        && f.column === filter.column
        && f.value === filter.value
    );

    if (existingIndex >= 0) {
      // Toggle off - remove the filter
      this.activeFilters.splice(existingIndex, 1);
    } else {
      // Remove any existing filter from the same source widget before adding new one
      this.activeFilters = this.activeFilters.filter(
        f => f.sourceWidgetKey !== filter.sourceWidgetKey
      );
      this.activeFilters.push(filter);
    }

    this.activeFilters$.next([...this.activeFilters]);
    this.recomputeFilteredData();
  }

  /**
   * Clear all interactive filters and restore master data.
   */
  clearAllInteractiveFilters(): void {
    this.activeFilters = [];
    this.activeFilters$.next([]);
    this.recomputeFilteredData();
  }

  /**
   * Re-filter master data and push to all widget streams.
   * The source widget that emitted the filter gets unfiltered data
   * (standard cross-filter UX).
   */
  private recomputeFilteredData(): void {
    if (this.activeFilters.length === 0) {
      // No filters active - push master data to all streams
      for (const [apiKey, data] of this.masterData.entries()) {
        const stream = this.dataStreams.get(apiKey);
        if (stream) stream.next(data);
      }
      return;
    }

    for (const [apiKey, masterRows] of this.masterData.entries()) {
      // Check if this api_key belongs to a source widget
      const isSourceWidget = apiKey !== PageDataService.COMMON_KEY && this.widgetApiConfigs.some(
        cfg => cfg.apiKey === apiKey
          && this.activeFilters.some(f => f.sourceWidgetKey === cfg.widgetKey)
      );

      let filteredData: any[];
      if (isSourceWidget) {
        // Source widget gets unfiltered data
        filteredData = masterRows;
      } else {
        // Apply all active filters (AND logic)
        filteredData = masterRows.filter((row: any) => {
          return this.activeFilters.every(filter => {
            if (filter.operator === 'in' && Array.isArray(filter.value)) {
              return filter.value.includes(row[filter.column]);
            }
            return row[filter.column] === filter.value;
          });
        });
      }

      const stream = this.dataStreams.get(apiKey);
      if (stream) stream.next(filteredData);
    }
  }

  /**
   * Check if a specific widget has an active filter.
   */
  hasActiveFilter(widgetKey: number): boolean {
    return this.activeFilters.some(f => f.sourceWidgetKey === widgetKey);
  }

  ngOnDestroy(): void {
    // Complete all BehaviorSubjects
    for (const stream of this.dataStreams.values()) {
      stream.complete();
    }
    this.dataStreams.clear();
    this.masterData.clear();
    this.loading$.complete();
    this.activeFilters$.complete();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
